import { GapCategory, type GapReviewExecutionSummary } from '@momentum/types';

import { shortDate } from './format';

/**
 * What the queue's run picker lists, and what a typed query matches against.
 *
 * Kept out of the component for the same reason the queue's own selection is:
 * "which runs are on offer" is a question with an answer worth testing on its
 * own, separately from the popover that draws them.
 */

/** The picker's pipeline filter. `all` is the default — both pipelines. */
export type RunVariantFilter = 'all' | GapCategory;

export const RUN_VARIANT_FILTERS: RunVariantFilter[] = [
  'all',
  GapCategory.SALES,
  GapCategory.DELIVERY,
];

export const RUN_VARIANT_LABELS: Record<RunVariantFilter, string> = {
  all: 'All',
  [GapCategory.SALES]: 'Sales',
  [GapCategory.DELIVERY]: 'Delivery',
};

/** One run as the picker draws it, plus the text a query is matched against. */
export interface RunOption {
  /** The run's own id — `exec-20260805T112411Z-2674`. */
  executionId: string;
  variant: GapCategory;
  /** Every scope the run touched, joined. A run can span more than one. */
  scopeNames: string;
  completedAt: Date | string;
  draftCount: number;
  /** The newest run of the list, which the picker marks so it can be found. */
  isLatest: boolean;
  /**
   * Everything the row shows, lowercased and joined — the id, the pipeline, the
   * scopes and the date. Matching the WHOLE row is what makes typing `2674`, or
   * `sodra`, or `aug` all narrow the list: the reviewer types whatever fragment
   * of the run they happen to remember.
   */
  searchText: string;
}

export function toRunOptions(
  executions: GapReviewExecutionSummary[]
): RunOption[] {
  return executions.map((execution, position) => {
    const scopeNames = execution.scopes
      .map((scope) => scope.scopeName)
      .join(', ');

    return {
      executionId: execution.executionId,
      variant: execution.variant,
      scopeNames,
      completedAt: execution.completedAt,
      draftCount: execution.draftCount,
      isLatest: position === 0,
      searchText: [
        execution.executionId,
        execution.variant,
        scopeNames,
        shortDate(execution.completedAt),
      ]
        .join(' ')
        .toLowerCase(),
    };
  });
}

export interface RunOptionQuery {
  variant: RunVariantFilter;
  /** Raw, as typed. Trimmed and lowercased here rather than by the caller. */
  query: string;
}

export function filterRunOptions(
  options: RunOption[],
  { variant, query }: RunOptionQuery
): RunOption[] {
  const needle = query.trim().toLowerCase();

  return options.filter(
    (option) =>
      (variant === 'all' || option.variant === variant) &&
      (needle === '' || option.searchText.includes(needle))
  );
}

/**
 * How many runs each pipeline chip would leave, so a chip can say so before it
 * is pressed. Counted over every run rather than over the typed query: the chip
 * is what the reviewer reaches for INSTEAD of typing, and a count that moved
 * with the search box would be a count of the search box.
 */
export function countRunsByVariant(
  options: RunOption[]
): Record<RunVariantFilter, number> {
  return {
    all: options.length,
    [GapCategory.SALES]: options.filter(
      (option) => option.variant === GapCategory.SALES
    ).length,
    [GapCategory.DELIVERY]: options.filter(
      (option) => option.variant === GapCategory.DELIVERY
    ).length,
  };
}

/**
 * Starred runs first, everything else in the order it already had.
 *
 * A stable partition rather than a sort key, because the list's existing order
 * is newest-first and that is still the right order INSIDE each group: pinning
 * is meant to lift a handful of runs above the tide of new ones, not to
 * rearrange the tide. Applied after {@link filterRunOptions} rather than before,
 * so a starred run that does not match the typed query stays hidden — the star
 * pins a run to the top of the list, it does not exempt it from the search.
 */
export function sortRunOptionsByFavorite(
  options: RunOption[],
  favoriteRunIds: ReadonlySet<string>
): RunOption[] {
  const favorites = options.filter((option) =>
    favoriteRunIds.has(option.executionId)
  );
  const rest = options.filter(
    (option) => !favoriteRunIds.has(option.executionId)
  );

  return [...favorites, ...rest];
}
